/**
 * SlideLightbox.tsx
 *
 * Full-screen modal that renders a realistic 16:9 preview of a slide
 * with actual text content positioned in the layout and generated images
 * shown in place.  Visual shell mirrors ImageLightbox (dark overlay,
 * prev/next arrows, counter, Escape to close).
 */

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faXmark } from '@fortawesome/free-solid-svg-icons';

import type { SlideConfig, ImageSlotState } from '@/types/wizardV2';
import { SlideFullPreview } from './SlideFullPreview';
import styles from './SlideLightbox.module.css';

interface Props {
  slides: SlideConfig[];
  openIndex: number | null;
  onClose: () => void;
  getPlantillaBgUrl: (slide: SlideConfig, index: number) => string | undefined;
  imageSlots?: Record<string, ImageSlotState>;
}

export const SlideLightbox = ({
  slides,
  openIndex,
  onClose,
  getPlantillaBgUrl,
  imageSlots,
}: Props) => {
  const isOpen = openIndex !== null && slides.length > 0;
  const [index, setIndex] = useState(openIndex ?? 0);

  useEffect(() => {
    if (openIndex !== null) setIndex(openIndex);
  }, [openIndex]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, goPrev, goNext]);

  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const slide = slides[index];
  if (!slide) return null;

  const hasMany = slides.length > 1;

  const imageUrls: (string | null)[] = [];
  const imgCount = slide.images?.length ?? 1;
  for (let j = 0; j < Math.max(imgCount, 1); j++) {
    const slotState = imageSlots?.[`${index}-${j}`];
    const url = slotState?.status === 'done' ? slotState.url : (slide.images?.[j]?.url ?? null);
    imageUrls.push(url ?? null);
  }
  const imageUrl = imageUrls[0];

  const lightbox = (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Slide macro view"
    >
      <button
        className={styles.closeBtn}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
        type="button"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>

      {hasMany && (
        <>
          <button
            className={`${styles.navBtn} ${styles.navPrev}`}
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous slide"
            type="button"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            className={`${styles.navBtn} ${styles.navNext}`}
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next slide"
            type="button"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </>
      )}

      <div className={styles.content} onClick={(e) => e.stopPropagation()}>
        <div className={styles.previewWrapper}>
          <SlideFullPreview
            slide={slide}
            plantillaBgUrl={getPlantillaBgUrl(slide, index)}
            slideNumber={index + 1}
            imageUrl={imageUrl}
            imageUrls={imageUrls}
          />
        </div>
      </div>

      {hasMany && (
        <div className={styles.counter} onClick={(e) => e.stopPropagation()}>
          {index + 1} / {slides.length}
        </div>
      )}
    </div>
  );

  return createPortal(lightbox, document.body);
};
